import type { ApolloClient } from "@apollo/client";
import type { IntlShape } from "react-intl";
import { createRun } from "@/components/save/profile-merge-commit";
import {
  ContentSharingDocument,
  CreateCompanyDocument,
  CreateOrganizationalRecordDocument,
  CreateOrganizationalRecordEntryDocument,
  DegreesDocument,
  OrganizationalRecordDocument,
  SetCompanyCompetencyDocument,
  StudioCreatePersonDocument,
} from "@/graphql/generated/operations";
import { OrganizationalRecordEntryKind, type ValidationError } from "@/graphql/generated/types";
import { validationResultMessage } from "@/lib/graphql-error";
import {
  normalize,
  placeholderEmailPrimary,
  studioPersonInput,
  toMonthYear,
  type StudioCompanyProposal,
  type StudioPersonProposal,
  type StudioProposal,
} from "@/lib/studio-proposal";
import { userErrorMessage } from "@/lib/user-error";
import { companyDraftError, EMPTY_COMPANY_DRAFT } from "@/pages/company/company-profile-draft";
import { companyCreateOutcome, companyCreateVariables, type CompanyCreateSource } from "@/pages/company/use-company-creation";
import {
  organizationalRecordSubjectVariables,
  type OrganizationalRecordSubject,
} from "@/pages/organizational-record/organizational-record-subject";

// Creating what the Studio proposal card shows (@external-adr AGENT-SERVICE-0036-the-studio-proposes-records-the-user-decides),
// with the user's own session and rights, after their click. A person is created first, then each position,
// education and expertise the card takes over, exactly as an import writes them (createRun): genesis can check
// those only once the person exists. A company goes through the company page's create, so the creator becomes its
// first admin as there, and then gets its expertise the way the company page adds it (setCompanyCompetency).

export type StudioClient = Pick<ApolloClient, "mutate" | "query">;

/** What has succeeded for one person in this session, so a second click continues instead of starting over. */
export interface PersonProgress {
  person: boolean;
  rows: Set<string>;
}

export type PersonOutcome =
  | { ok: true; rowFailures: string[] }
  | { ok: false; issues: { field?: string | null; error?: ValidationError | null }[] }
  | { ok: false; error: unknown };

export interface DegreeOption {
  id: string;
  name?: string | null;
  localizedName?: { localized?: string | null } | null;
}

// A degree is taken over only as a catalogue entry, and only when the notes name one exactly; otherwise the
// education is created without it.
export function degreeIdFor(degree: string | undefined, degrees: DegreeOption[]): string | null {
  if (!degree) return null;
  const wanted = normalize(degree);
  const names = (option: DegreeOption) => [option.name, option.localizedName?.localized];
  return degrees.find((option) => names(option).some((name) => name && normalize(name) === wanted))?.id ?? null;
}

// One row write, checked: genesis answers a refused row with a Validate* result rather than an error.
async function rowFailure(run: () => Promise<unknown>, intl: IntlShape, fallback: string): Promise<string | null> {
  try {
    const result = (await run()) as { data?: Record<string, unknown> | null } | null | undefined;
    for (const payload of Object.values(result?.data ?? {})) {
      const typename = (payload as { __typename?: string } | null)?.__typename;
      if (typename?.startsWith("Validate")) return validationResultMessage(payload as Parameters<typeof validationResultMessage>[0], fallback);
    }
    return null;
  } catch (error) {
    return userErrorMessage(intl, error);
  }
}

export async function createStudioPerson({ client, intl, person, personId, creatorId, progress }: {
  client: StudioClient;
  intl: IntlShape;
  person: StudioPersonProposal;
  personId: string;
  creatorId: string | undefined;
  progress: PersonProgress;
}): Promise<PersonOutcome> {
  if (!progress.person) {
    try {
      const result = await client.mutate({
        mutation: StudioCreatePersonDocument,
        variables: { input: studioPersonInput(person, personId, false, placeholderEmailPrimary(creatorId)) },
      });
      const out = result.data?.createPerson;
      if (out?.__typename === "ValidatePersonResult") return { ok: false, issues: out.issues };
      if (out?.__typename !== "CreatePersonResult") return { ok: false, error: out };
    } catch (error) {
      return { ok: false, error };
    }
    progress.person = true;
  }

  const fallback = intl.formatMessage({ id: "studio.proposal.rowFailed", defaultMessage: "An entry could not be taken over." });
  const educations = person.educations.filter((education) => !education.problem);
  const degrees = educations.some((education) => education.degree)
    ? ((await client.query({ query: DegreesDocument }).catch(() => null))?.data?.degrees.degrees ?? [])
    : [];
  let defaultRecipients: { id: string }[];
  try {
    const sharing = await client.query({ query: ContentSharingDocument, fetchPolicy: "network-only" });
    if (!sharing.data?.defaultContentSharingRecipients) throw new Error("Missing content sharing configuration");
    defaultRecipients = sharing.data.defaultContentSharingRecipients.map(({ id }) => ({ id }));
  } catch (error) {
    return { ok: false, error };
  }
  const rows: { key: string; write: ReturnType<typeof createRun> }[] = [
    ...person.experiences.flatMap((experience, index) => (experience.problem ? [] : [{
      key: `x${index}`,
      write: createRun(client, personId, "experiences", (field) => ({
        title: experience.jobTitle,
        employer: experience.employer,
        location: experience.location,
        startDate: toMonthYear(experience.start),
        endDate: toMonthYear(experience.end),
      })[field] || null, 0, defaultRecipients),
    }])),
    ...person.educations.flatMap((education, index) => (education.problem ? [] : [{
      key: `e${index}`,
      write: createRun(client, personId, "educations", (field) => ({
        fieldOfStudy: education.field,
        institution: education.institution,
        degree: degreeIdFor(education.degree, degrees) ?? undefined,
        startDate: toMonthYear(education.start),
        endDate: toMonthYear(education.end),
      })[field] || null, 0, defaultRecipients),
    }])),
    ...(person.expertises ?? []).map((expertise, index) => ({
      key: `k${index}`,
      write: createRun(client, personId, "expertises", (field) => (field === "skill" ? expertise.description : null), index, defaultRecipients),
    })),
  ];
  const pending = rows.filter((row) => !progress.rows.has(row.key));
  const failures = await Promise.all(pending.map(async (row) => {
    const failed = row.write ? await rowFailure(() => row.write!(false), intl, fallback) : fallback;
    if (failed === null) progress.rows.add(row.key);
    return failed;
  }));
  return { ok: true, rowFailures: [...new Set(failures.filter((failure): failure is string => failure !== null))] };
}

export function studioCompanySource(company: StudioCompanyProposal): CompanyCreateSource {
  return {
    profile: {
      ...EMPTY_COMPANY_DRAFT,
      name: company.name,
      employeeCount: company.employeeCount === undefined ? "" : String(company.employeeCount),
      summary: company.summary ?? "",
    },
    logo: null,
  };
}

export async function createStudioCompany(client: StudioClient, intl: IntlShape, company: StudioCompanyProposal) {
  const source = studioCompanySource(company);
  const invalid = companyDraftError(intl, source.profile);
  if (invalid) return { ok: false as const, message: invalid };
  try {
    const result = await client.mutate({ mutation: CreateCompanyDocument, variables: companyCreateVariables(source, false) });
    return companyCreateOutcome(intl, result.data?.createCompany);
  } catch (error) {
    return { ok: false as const, message: userErrorMessage(intl, error) };
  }
}

/**
 * The expertise of a company that exists: one `setCompanyCompetency` per label, as the company page adds them.
 * genesis refuses a label the company already lists, so labels are sent once, and `done` (per company, for this
 * session) keeps a second click from sending one again. Answers why the refused ones were refused.
 */
export async function addStudioCompanyExpertises({ client, intl, companyId, company, done }: {
  client: StudioClient;
  intl: IntlShape;
  companyId: string;
  company: StudioCompanyProposal;
  done: Set<string>;
}): Promise<string[]> {
  const labels = new Map<string, string>();
  for (const expertise of company.expertises ?? []) {
    const label = expertise.description.trim();
    if (label && !labels.has(normalize(label))) labels.set(normalize(label), label);
  }
  const failures: string[] = [];
  for (const [key, label] of labels) {
    if (done.has(key)) continue;
    try {
      await client.mutate({ mutation: SetCompanyCompetencyDocument, variables: { input: { companyId, label } } });
      done.add(key);
    } catch (error) {
      failures.push(userErrorMessage(intl, error));
    }
  }
  return [...new Set(failures)];
}

/**
 * The provenance note (spark #311, #947): where a record the card created came from, in the record's own words. A
 * NOTE in the subject's organizational record with the notebook, the day and the passages the entry cited, so a
 * record from an unconfirmed research reads as one and not as kept master data. Quoted as they read when the
 * proposal came (`proposal.sources`), because the notebook's ids move on.
 */
export function provenanceNote(intl: IntlShape, proposal: StudioProposal, sourceNoteIds: string[], createdAt: Date) {
  const date = intl.formatDate(createdAt, { dateStyle: "medium" });
  const intro = proposal.notebookName?.trim()
    ? intl.formatMessage(
        { id: "studio.provenance.intro", defaultMessage: "Created from the Studio notebook “{notebook}” on {date}." },
        { notebook: proposal.notebookName.trim(), date },
      )
    : intl.formatMessage({ id: "studio.provenance.introUnnamed", defaultMessage: "Created from the Studio on {date}." }, { date });
  const quoted = (proposal.sources ?? [])
    .filter((source) => sourceNoteIds.includes(source.id))
    .map((source) => source.text.trim().split("\n").map((line) => `> ${line}`).join("\n"));
  const sources = quoted.length
    ? [`**${intl.formatMessage({ id: "studio.provenance.sources", defaultMessage: "The passages it was taken from" })}**`, ...quoted].join("\n\n")
    : intl.formatMessage({ id: "studio.provenance.noSources", defaultMessage: "The card kept no passages to quote." });
  return {
    title: intl.formatMessage({ id: "studio.provenance.title", defaultMessage: "Taken over from the Studio" }),
    content: `${intro}\n\n${sources}`,
  };
}

/**
 * Writes the provenance note into the subject's organizational record, creating the record where it has none.
 * Deliberately not part of creating the record itself: a failure is named on the card and can be repeated.
 */
export async function fileStudioProvenance({ client, intl, subject, proposal, sourceNoteIds, createdAt = new Date() }: {
  client: StudioClient;
  intl: IntlShape;
  subject: OrganizationalRecordSubject;
  proposal: StudioProposal;
  sourceNoteIds: string[];
  createdAt?: Date;
}): Promise<{ ok: true; entryId: string } | { ok: false; message: string }> {
  try {
    const variables = organizationalRecordSubjectVariables(subject);
    const existing = await client.query({ query: OrganizationalRecordDocument, variables, fetchPolicy: "network-only" });
    const recordId = existing.data?.organizationalRecord?.id
      ?? (await client.mutate({ mutation: CreateOrganizationalRecordDocument, variables })).data!.createOrganizationalRecord.id;
    const note = provenanceNote(intl, proposal, sourceNoteIds, createdAt);
    const entry = await client.mutate({
      mutation: CreateOrganizationalRecordEntryDocument,
      fetchPolicy: "no-cache",
      variables: { input: { recordId, kind: OrganizationalRecordEntryKind.Note, title: note.title, content: note.content, links: [] } },
    });
    return { ok: true, entryId: entry.data!.createOrganizationalRecordEntry.id };
  } catch (error) {
    return { ok: false, message: userErrorMessage(intl, error) };
  }
}
